import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestCounter = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="bg-[#C8A951]/10 rounded-2xl p-8 text-center"
      data-testid="guest-counter"
    >
      <div className="flex items-center justify-center gap-3 mb-4">
        <Users className="w-8 h-8 text-[#C8A951]" />
        <h3 className="font-serif text-2xl text-[#3B2F2F]">Join the Celebration</h3>
      </div>
      
      <p className="text-[#5A4A4A] leading-relaxed max-w-md mx-auto">
        We would be honored to celebrate this special day with you.
        Your presence will make our joy complete as we begin this new chapter together.
      </p>
      
      <div className="mt-6">
        <a 
          href="#rsvp" 
          className="btn-gold inline-block"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          RSVP Now
        </a>
      </div>
    </motion.div>
  );
};

const GuestBook = () => {
  const [wishes, setWishes] = useState([]);
  const [newWish, setNewWish] = useState({ name: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchWishes();
    // Poll every 20s so admin changes (edit / hide / delete) appear without a redeploy.
    const t = setInterval(fetchWishes, 20000);
    // Refresh when the tab regains focus (common on mobile)
    const onVis = () => { if (document.visibilityState === 'visible') fetchWishes(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const fetchWishes = async () => {
    try {
      const response = await axios.get(`${API}/guestbook`);
      setWishes(response.data);
    } catch (error) {
      console.error('Error fetching wishes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newWish.name.trim() || !newWish.message.trim()) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/guestbook`, newWish);
      setNewWish({ name: '', message: '' });
      setSubmitted(true);
      fetchWishes();
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting wish:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="guestbook" className="section bg-cream" data-testid="guestbook-section">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">Leave Your Wishes</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight">
            Guest Book
          </h2>
          <div className="gold-divider" />
          <p className="text-[#5A4A4A] mt-4">
            Share a note of love, a blessing, or a few words of wisdom for Mary &amp; Arrington.
          </p>
        </motion.div>

        {/* Submit Wish Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="wedding-card mb-12"
        >
          {submitted ? (
            <div className="text-center py-4">
              <Heart className="w-12 h-12 text-[#C8A951] mx-auto mb-3" fill="#C8A951" />
              <p className="font-serif text-xl text-[#3B2F2F]">Thank you for your wishes!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Your Name</label>
                <input
                  type="text"
                  value={newWish.name}
                  onChange={(e) => setNewWish(prev => ({ ...prev, name: e.target.value }))}
                  className="wedding-input"
                  placeholder="Enter your name"
                  required
                  data-testid="guestbook-name-input"
                />
              </div>
              <div>
                <label className="block text-[#3B2F2F] text-sm font-medium mb-2">Your Message</label>
                <textarea
                  value={newWish.message}
                  onChange={(e) => setNewWish(prev => ({ ...prev, message: e.target.value }))}
                  className="wedding-input resize-none"
                  rows={4}
                  placeholder="Write your wishes, blessings, or advice for Mary & Arrington..."
                  required
                  data-testid="guestbook-message-input"
                />
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-gold inline-flex items-center gap-2"
                  data-testid="guestbook-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Your Wishes
                </button>
              </div>
            </form>
          )}
        </motion.div>

        {/* Display Wishes */}
        {wishes.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            {wishes.slice(0, 8).map((wish, index) => (
              <motion.div
                key={wish.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="wedding-card relative"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#C8A951] rounded-full flex items-center justify-center">
                  <Heart className="w-4 h-4 text-white" fill="white" />
                </div>
                <p className="text-[#5A4A4A] italic mb-4 leading-relaxed">&ldquo;{wish.message}&rdquo;</p>
                <p className="font-serif text-[#3B2F2F] text-right">— {wish.name}</p>

                {wish.reply && (
                  <div
                    className="mt-4 ml-4 pl-4 py-3 pr-4 border-l-2 border-[#C8A951] bg-[#F8F5EF]/70 rounded-r-lg"
                    data-testid={`wish-reply-${wish.id}`}
                  >
                    <p className="text-[#3B2F2F] leading-relaxed whitespace-pre-wrap">{wish.reply}</p>
                    <p className="font-cursive text-[#C8A951] text-lg text-right mt-1">
                      — Mary &amp; Arrington
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {wishes.length === 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-[#5A4A4A] italic"
          >
            Be the first to leave a heartfelt note for Mary &amp; Arrington
          </motion.p>
        )}
      </div>
    </section>
  );
};

export { GuestCounter, GuestBook };
